/************************************************************
// Purpose       : Root of Application
// Created by    : Yogesh
// Created Date  : 25-05-2025
// Description   : This is root of application and create routes.

// Change history:
// 25-05-2025 / Yogesh // Write Comments  

************************************************************/


import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Login from "./pages/auths/Login";
import Otp from "./pages/auths/otp/Otp";
import Layout from "./pages/layout/Layout";
import Dashboard from "./pages/dashboard/Dashboard";
import Tickets from "./pages/tickets/Tickets";
import Registration from "./pages/auths/Registration";
import Createticket from "./pages/tickets/Createticket";
import ViewDetails from "./pages/tickets/ViewDetails";
import ManageProfile from "./pages/profile/ManageProfile";
import Feedback from "./pages/feedback/Feedback";
import { setGeoLocation, setIdentityData } from "./utils/common";
import { ToastContainer } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { PrivateRoutes } from "./utils/privateRoutes";
import { handleLoader } from "./redux/layout";
import toastNotify from "./utils/toastNotify";
import Error404 from "./pages/error/404";
import SessionError from "./pages/error/SessionError";
import { handleBankLogo, handleBaseColor } from "./redux/layout";
import { getIsAutheticate } from "./utils/common";
import Leading from "./pages/auths/Leading";
import ConnectionError from "./pages/error/ConnectionError";
import Accouncement from "./pages/accouncement/Accouncement";
import Circular from "./pages/Circular/Circular";

import { handleNotificationLoader, handleNotificationData } from "./redux/Notification";
import { getBankDetails, getNotificationAlerts } from "./apiService/apiService";
import ForgotPwd from "./pages/auths/ForgotPwd";
import ChangePassword from "./pages/changePwd/ChangePassword";
import UserMannual from "./pages/userMannual/UserMannual";
import { Container } from "react-bootstrap";

export type userDataProps = {
  AccNoMandatory: string,
  BankAddress: string,
  BankCode: string,
  BankEmailId: string,
  BankMobileNo: string,
  BankName: string,
  BankPersonName: string,
  CbsTransactionDetailApiStatus: string,
  EmailId: string,
  FirstName: string,
  LastLogin: string,
  LastName: string,
  MobileNo: string,
  NickName: string,
  Photo: string,
  SessionToken: string,
  SubCatMandatory: string,
  TicketAttachmentUploadSize: string,
  TicketPermanentCloseStatus: string,
  TicketTranTblRowLimit: string,
  TranCode: string,
}

function App() {

  const dispatch = useDispatch();
  const dynamicColor = useSelector((state: any) => state.layout.baseColor);
  const isAuthenticate = getIsAutheticate();
  const [bankData, setBankData] = useState<any>(null);


  // Get Bank Detail
  const getBankDtl = useCallback(async () => {
    try {
      dispatch(handleLoader(true));
      const bankDtlVal = await getBankDetails();

      if (bankDtlVal) {
        setBankData(bankDtlVal?.response);

        dispatch(handleBaseColor(bankDtlVal?.baseColor));
        dispatch(handleBankLogo(bankDtlVal?.logos));
      }

    } catch (error) {
      //  Ignore Case
    } finally {
      dispatch(handleLoader(false));
    }
  }, [dispatch]);


  // Get Notification
  const fetchNotification = useCallback(async () => {
    try {
      dispatch(handleNotificationLoader(true));
      const notificationData = await getNotificationAlerts();
      dispatch(handleNotificationData(notificationData))
    } catch (error: any) {
      // Ignore Case  
    } finally {
      dispatch(handleNotificationLoader(false));
    }
  }, [dispatch]);

  useMemo(() => {
    if (isAuthenticate && window.location.pathname !== "/") {
      fetchNotification();
    }
  }, [isAuthenticate, fetchNotification])


  // Get Location of User
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.permissions.query({ name: 'geolocation' }).then((PermissionStatus) => {
        if (PermissionStatus.state === 'denied') {
        } else {
          setGeoLocation();
        }
      });
    } else {
      alert("Geolocation is not supported in your browser.");
    }

    (async () => {
      await setIdentityData();
      await getBankDtl();
    })();
  }, [])

  // Here write Dark mode functionlly
  const isDark = useSelector((state: any) => state.layout.isDark);

  useEffect(() => {
    if (isDark) {
      document.documentElement.style.setProperty('--ComponentBgColor', '#1e293b');
      document.documentElement.style.setProperty('--ComponentBgColor50', '#1e293b50');
      document.documentElement.style.setProperty('--bgColor', '#0f172a');
      document.documentElement.style.setProperty('--slate700Color', '#f7f8ff');
      document.documentElement.style.setProperty('--slate500Color', '#bfd5e190');
    } else {
      document.documentElement.style.setProperty('--ComponentBgColor', '');
      document.documentElement.style.setProperty('--bgColor', dynamicColor + '05');
      document.documentElement.style.setProperty('--slate700Color', '');
      document.documentElement.style.setProperty('--slate500Color', '');
    };
  }, [isDark]);


  useEffect(() => {
    if (dynamicColor !== '') {
      document.documentElement.style.setProperty('--primaryColor25', dynamicColor + '25');
      document.documentElement.style.setProperty('--primaryColor50', dynamicColor + '50');
      document.documentElement.style.setProperty('--primaryColor75', dynamicColor + '75');
      document.documentElement.style.setProperty('--primaryColor40', dynamicColor + '40');
    };
    document.documentElement.style.setProperty('--primaryColor', dynamicColor);
  }, [dynamicColor])

  //----------------------------------- Here Disable to right click and copy, paste and cut --------------------------------------
  useEffect(() => {
    // disable to right click and copy and paste
    const disableRightClick = (event: any) => {
      event.preventDefault();
      toastNotify("Right Click Disabled", "error");
    };

    const disableCopyPaste = (event: any) => {
      event.preventDefault();
      toastNotify("Copying and pasting are disabled on this website.", "error");
    };

    // Add event listeners
    document.addEventListener("contextmenu", disableRightClick);
    document.addEventListener("copy", disableCopyPaste);
    document.addEventListener("cut", disableCopyPaste);
    document.addEventListener("paste", disableCopyPaste);

    return function cleanup() {
      document.removeEventListener("contextmenu", disableRightClick);
      document.removeEventListener("copy", disableCopyPaste);
      document.removeEventListener("cut", disableCopyPaste);
      document.removeEventListener("paste", disableCopyPaste);
    }
  }, []);

  // ----------------------------------- Here write logic of sesssion out start  -----------------------------------------------
  useEffect(() => {
    const handleOffline = () => {
      window.location.href = "/connection";
    };
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, [])

  // ----------------------------------- Here write logic of sesssion out end  -----------------------------------------------

  return (
    <div>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={bankData ? <Login /> : <Leading />} />
          <Route path="/forgotpassword" element={<ForgotPwd />} />
          <Route path="/otp" element={<Otp />} />
          <Route path="/registration" element={<Registration />} />
          <Route path="*" element={<Error404 />} />
          <Route path="/sessionout" element={<SessionError />} />
          <Route path="/connection" element={<ConnectionError />} />
          <Route path="/loginusermannual" element={<Container className="mt-5"><UserMannual flag="L" /></Container>} />

          <Route path="/" element={<Layout />}>
            <Route path="dashboard" element={<PrivateRoutes children={<Dashboard />} />} />
            <Route path="tickets" element={<PrivateRoutes children={<Tickets />} />} />
            <Route path="createticket" element={<PrivateRoutes children={<Createticket />} />} />
            <Route path="tickets/createticket" element={<PrivateRoutes children={<Createticket />} />} />
            <Route path="tickets/viewdetails" element={<PrivateRoutes children={<ViewDetails />} />} />
            <Route path="announcement" element={<PrivateRoutes children={<Accouncement />} />} />
            <Route path="circular" element={<PrivateRoutes children={<Circular />} />} />
            <Route path="profile" element={<PrivateRoutes children={<ManageProfile />} />} />
            <Route path="feedback" element={<PrivateRoutes children={<Feedback />} />} />
            <Route path="changepwd" element={<PrivateRoutes children={<ChangePassword />} />} />
            <Route path="usermannual" element={<UserMannual />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <ToastContainer />
    </div>
  );
}

export default App;
